import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  isBindV3,
  resolveDocumentV3,
  resolvePath,
  setValueAtPath,
  type BlockNode,
  type DocumentV3,
} from '@docgen/schema';
import type { EditEvent } from '@docgen/sdk';
import { BLOCKS_BY_TYPE } from '@docgen/blocks';
import { PanelRightClose, PanelRightOpen } from 'lucide-react';
import {
  deleteDocument,
  getProposal,
  getReadiness,
  resetProposal,
  saveDocument,
  type PricingResult,
  type ProposalRecord,
  type ReadinessIssue,
} from '../lib/api';
import { ConfirmDialog, LoadingState, useNarrowScreen } from '@bcecloud/portal-ui';
import { FEATURES } from '@/lib/flags';
import { APP_FEATURE, useAppFeatures } from '@/lib/app-features';
import { requestAssistantOpen } from '@/components/assistant/assistant-store';
import { PhoneBuilder } from '../phone/PhoneBuilder';
import { ExportIssuesNotice } from './ExportIssuesNotice';
import { errorMessage, uploadMedia } from '@/lib/api';
import { onProposalChanged } from '../lib/live';
import { usePersistedBoolean } from '../lib/persisted';
import {
  concreteBindPath,
  duplicateBlock,
  findBlock,
  insertBlock,
  moveBlock,
  newBlockId,
  removeBlock,
  setBlockProp,
  setDocData,
  recordOverride,
  withServerDerivedData,
} from '../lib/doc-ops';
import { useDocHistory } from '../lib/history';
import { DocumentView } from '../document/DocumentView';
import { RichTextEditor } from '../document/RichTextEditor';
import { probeDropTarget, type DropTarget } from '../document/hit-test';
import { Desk, type Zoom } from './Desk';
import { HoverGutter, InsertMenu } from './HoverControls';
import { Inspector } from './Inspector';
import { ImageUploadContext, type ImageUpload } from './image-upload';
import { Panel, type PanelLibrary } from './Panel';
import { LibraryInsertNotice, type LibraryInsertReport } from '../library/LibraryInsertNotice';
import { insertLibraryItem, saveLibraryItem, suggestLibraryDetails } from '../lib/page-library';
import { registerOpenEditor } from '../lib/editor-bridge';
import { insertIndexAfterSelection } from '../lib/library-pages';
import { PricingPanel } from './PricingPanel';
import { TopBar, type EditorAssistantToggle } from './TopBar';
import { salesforceOpportunityUrl } from '@/lib/salesforce';
import type { ActiveDrag, SaveState, Selection } from './types';

/**
 * Media `kind` for images placed in proposals, so the bucket groups them. Must
 * match the API's PROPOSAL_IMAGE_MEDIA_KIND: it is the only kind the
 * Salesforce-embedded editor may upload.
 */
const PROPOSAL_IMAGE_KIND = 'proposal-image';

const AUTOSAVE_MS = 1500;

/** The two proposal-wide actions that ask before they run. */
const PROPOSAL_ACTION = { reset: 'reset', delete: 'delete' } as const;
type ProposalAction = (typeof PROPOSAL_ACTION)[keyof typeof PROPOSAL_ACTION];

/** What each action's confirmation says. */
const ACTION_COPY: Record<ProposalAction, { title: string; message: string; confirmLabel: string }> = {
  [PROPOSAL_ACTION.reset]: {
    title: 'Reset this proposal to the template?',
    message:
      'Every page goes back to the standard BCE template, with the client, event and salesperson filled in again. Your pricing (options, programs and discounts) stays and is placed back on the new pages. Edits to the pages are replaced.',
    confirmLabel: 'Reset to template',
  },
  [PROPOSAL_ACTION.delete]: {
    title: 'Delete this proposal?',
    message: 'It disappears from Proposals for everyone. Ask an admin if it needs to come back.',
    confirmLabel: 'Delete proposal',
  },
};

/**
 * How the editor behaves inside Salesforce (`/embed/proposals/:id`), where it
 * runs on a scoped Editor token rather than a staff session: no app
 * navigation, no delete, a Done button, and the host told about every save.
 * The host may also hold the assistant panel and pass its toggle.
 */
export interface EditorEmbedding {
  /** The server now holds a new version (autosave, pricing, library insert, reset). */
  readonly onSaved: () => void;
  /** The proposal could not be loaded (the session ended, or it is gone). */
  readonly onLoadError: (error: unknown) => void;
  /** Done was pressed and every pending edit is saved. */
  readonly onDone: () => void;
  /** Exports the PDF in place; the print view's new tab would have no session. */
  readonly onExportPdf: () => void;
  /** The host's assistant panel toggle, shown in the top bar. */
  readonly assistant?: EditorAssistantToggle;
}

export interface EditorProps {
  /** Present only in the Salesforce embed. */
  readonly embedding?: EditorEmbedding;
}

export function Editor({ embedding }: EditorProps = {}) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isPhone = useNarrowScreen();
  const isFeatureOn = useAppFeatures();
  const libraryOn = isFeatureOn(APP_FEATURE.pageLibrary);
  const history = useDocHistory();
  const doc = history.doc;
  const [selection, setSelection] = useState<Selection>(null);
  /** The selection as of the latest render, for an insert BESTie starts. */
  const selectionRef = useRef<Selection>(null);
  selectionRef.current = selection;
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(true);
  const [zoom, setZoom] = useState<Zoom>('fit');
  const [saveState, setSaveState] = useState<SaveState>('saved');
  /** The proposal's non-document state (pricing, totals, Salesforce sync). */
  const [meta, setMeta] = useState<Omit<ProposalRecord, 'document'> | null>(null);
  const [pricingOpen, setPricingOpen] = useState(false);
  /** The reset or delete waiting on its confirmation, and how it is going. */
  const [pending, setPending] = useState<{ action: ProposalAction; busy: boolean; error: string | null } | null>(null);
  const [inspectorCollapsed, setInspectorCollapsed] = usePersistedBoolean('proposal.panel.right.collapsed', false);
  const [activeDrag, setActiveDrag] = useState<ActiveDrag | null>(null);
  const [dropTarget, setDropTarget] = useState<DropTarget | null>(null);
  const [paneWidth, setPaneWidth] = useState<number>(() => {
    try {
      const stored = Number(localStorage.getItem('dg.paneWidth'));
      return stored >= 260 && stored <= 600 ? stored : 320;
    } catch {
      return 320;
    }
  });
  const [gutterLock, setGutterLock] = useState(false);
  const [insertMenu, setInsertMenu] = useState<{ blockId: string; at: { top: number; left: number } } | null>(null);
  const lastHoverRef = useRef<string | null>(null);

  const deskRef = useRef<HTMLDivElement>(null);
  const dropTargetRef = useRef<DropTarget | null>(null);
  const savedDocRef = useRef<DocumentV3 | null>(null);
  /** The document as of the latest render, read after an await. */
  const latestDocRef = useRef<DocumentV3 | null>(null);
  latestDocRef.current = doc;
  /** What stops a PDF export (sample text, empty required fields); null until known. */
  const [exportIssues, setExportIssues] = useState<readonly ReadinessIssue[] | null>(null);
  const [showExportIssues, setShowExportIssues] = useState(false);
  /** After a library insert that kept some of this proposal's own data: which item, and which keys. */
  const [insertReport, setInsertReport] = useState<LibraryInsertReport | null>(null);

  const resolved = useMemo(() => (doc ? resolveDocumentV3(doc) : null), [doc]);

  const apply = useCallback(
    (next: DocumentV3, coalesceKey?: string) => {
      history.set(next, coalesceKey);
      setSaveState('dirty');
    },
    [history],
  );

  /**
   * Swaps in a document the server already holds (a pricing change re-projects
   * it): history restarts there, and the saved ref is moved so the autosave
   * does not PUT the server's own document straight back.
   */
  const replaceDoc = useCallback(
    (next: DocumentV3) => {
      history.reset(next);
      savedDocRef.current = next;
      setSaveState('saved');
    },
    [history],
  );

  /** Tells the Salesforce host the server holds a new version (no-op in the app). */
  const onSavedRef = useRef(embedding?.onSaved);
  onSavedRef.current = embedding?.onSaved;
  const notifySaved = useCallback(() => onSavedRef.current?.(), []);

  const onPricingResult = useCallback(
    (result: PricingResult) => {
      setMeta(result.proposal);
      replaceDoc(result.proposal.document);
      notifySaved();
    },
    [replaceDoc, notifySaved],
  );

  /** Runs the confirmed reset or delete. */
  const runPending = useCallback(async () => {
    if (!id || !pending) return;
    setPending({ ...pending, busy: true, error: null });
    try {
      if (pending.action === PROPOSAL_ACTION.delete) {
        await deleteDocument(id);
        navigate('/proposals', { replace: true });
        return;
      }
      const reset = await resetProposal(id);
      setMeta(reset);
      replaceDoc(reset.document);
      notifySaved();
      setSelection(null);
      setPending(null);
    } catch (error) {
      setPending({ ...pending, busy: false, error: errorMessage(error) });
    }
  }, [id, pending, navigate, replaceDoc, notifySaved]);

  /* ---------------- load ---------------- */

  const onLoadErrorRef = useRef(embedding?.onLoadError);
  onLoadErrorRef.current = embedding?.onLoadError;

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    getProposal(id)
      .then((loaded) => {
        if (cancelled) return;
        history.reset(loaded.document);
        savedDocRef.current = loaded.document;
        setMeta(loaded);
        setSelection(null);
        setSaveState('saved');
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (onLoadErrorRef.current) onLoadErrorRef.current(error);
        else navigate('/proposals', { replace: true });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  /* ---------------- live refresh (assistant edits this proposal) ---------------- */

  /**
   * The assistant edits proposals directly through its tools; when it finishes a
   * change to the proposal this editor is showing, refetch the server's document
   * and swap it in. `replaceDoc` moves the saved ref, so the pending autosave
   * does not fight the assistant's write. In-flight local edits (`saving`/`dirty`)
   * are left alone — the autosave re-projects and wins — to avoid clobbering what
   * the user is typing.
   */
  useEffect(() => {
    if (!id) return undefined;
    return onProposalChanged((changedId) => {
      if (changedId !== id) return;
      setSaveState((state) => {
        if (state === 'saving' || state === 'dirty') return state;
        getProposal(id)
          .then((loaded) => {
            setMeta(loaded);
            replaceDoc(loaded.document);
          })
          .catch(() => undefined);
        return state;
      });
    });
  }, [id, replaceDoc]);

  /* ---------------- inline edits (write through bindings) ---------------- */

  const onDocEdit = useCallback(
    (event: EditEvent) => {
      const current = history.doc;
      if (!current) return;
      const raw = findBlock(current, event.blockId)?.block.props[event.prop];
      const suffix = event.blockKey.startsWith(event.blockId)
        ? event.blockKey.slice(event.blockId.length)
        : '';
      const bound = isBindV3(raw) && raw.$bind !== '';

      if (event.type === 'slot-edit') {
        if (bound && isBindV3(raw)) {
          const base = concreteBindPath(current, event.blockId, raw.$bind, suffix);
          const path = event.itemPath !== undefined ? `${base}.${event.itemPath}` : base;
          const previous = resolvePath(current.data, path);
          const value =
            typeof previous === 'number' && !Number.isNaN(Number(event.value))
              ? Number(event.value)
              : event.value;
          // Remembered as an override too: the server regenerates some data on
          // every save (pricing, program names, contents) and puts these back.
          apply(recordOverride(setDocData(current, path, value), path, value), `data:${path}`);
        } else if (event.itemPath !== undefined) {
          // literal list value: set inside the prop's array
          const next = setValueAtPath({ v: raw }, `v.${event.itemPath}`, event.value)['v'];
          apply(
            setBlockProp(current, event.blockId, event.prop, next),
            `prop:${event.blockId}:${event.prop}:${event.itemPath}`,
          );
        } else {
          apply(
            setBlockProp(current, event.blockId, event.prop, event.value),
            `prop:${event.blockId}:${event.prop}`,
          );
        }
        return;
      }

      // list-edit: structural add/remove on the array (bound → in data)
      const applyListOp = (arr: unknown[]): unknown[] =>
        event.op === 'insert'
          ? [
              ...arr.slice(0, event.index),
              structuredClone(event.item ?? {}),
              ...arr.slice(event.index),
            ]
          : arr.filter((_, i) => i !== event.index);

      if (bound && isBindV3(raw)) {
        const base = concreteBindPath(current, event.blockId, raw.$bind, suffix);
        const arr = resolvePath(current.data, base);
        if (!Array.isArray(arr)) return;
        apply(setDocData(current, base, applyListOp(arr)));
      } else {
        const arr = Array.isArray(raw) ? raw : [];
        apply(setBlockProp(current, event.blockId, event.prop, applyListOp(arr)));
      }
    },
    [apply, history],
  );

  /* ---------------- saving ---------------- */

  /** Saves the document if it changed; resolves true once the server holds what's on screen. */
  const persist = useCallback(async (): Promise<boolean> => {
    const current = history.doc;
    if (!id || !current) return false;
    if (current === savedDocRef.current) return true;
    setSaveState('saving');
    try {
      const saved = await saveDocument(id, current);
      savedDocRef.current = current;
      setSaveState('saved');
      notifySaved();
      // The server rebuilds the intro and labels from the deal; show them now,
      // unless the person has typed on since this save started.
      const merged = withServerDerivedData(current, saved.document);
      if (merged !== current && latestDocRef.current === current) {
        history.set(merged, 'server-derived');
        savedDocRef.current = merged;
      }
      return true;
    } catch {
      setSaveState('error');
      return false;
    }
  }, [history, id, notifySaved]);

  useEffect(() => {
    if (!doc || doc === savedDocRef.current) return;
    const timer = window.setTimeout(() => {
      void persist();
    }, AUTOSAVE_MS);
    return () => window.clearTimeout(timer);
  }, [doc, persist]);

  /* ---------------- export readiness ---------------- */

  // Re-checked whenever the stored document settles (load, save, pricing, reset).
  useEffect(() => {
    if (!id || saveState !== 'saved') return undefined;
    let cancelled = false;
    getReadiness(id)
      .then((readiness) => {
        if (!cancelled) setExportIssues(readiness.issues);
      })
      // Unknown readiness never blocks: the print view enforces it server-side too.
      .catch(() => {
        if (!cancelled) setExportIssues(null);
      });
    return () => {
      cancelled = true;
    };
  }, [id, saveState, meta]);

  /* ---------------- reveal ---------------- */

  const revealBlock = useCallback((blockId: string) => {
    deskRef.current
      ?.querySelector(`[data-block="${CSS.escape(blockId)}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  const revealPage = useCallback((pageId: string) => {
    deskRef.current
      ?.querySelector(`[data-page="${CSS.escape(pageId)}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  /* ---------------- images ---------------- */

  /** Image blocks upload to the shared media bucket and keep its public URL. */
  const uploadProposalImage = useCallback<ImageUpload>(
    async (file) => (await uploadMedia(file, { kind: PROPOSAL_IMAGE_KIND })).url,
    [],
  );

  /* ---------------- page library ---------------- */

  /**
   * The server reads library pages from the saved proposal and inserts into
   * it, so both actions save any pending edits first. An insert then swaps in
   * the document the server saved, like a pricing change.
   */
  const library = useMemo<PanelLibrary | undefined>(() => {
    // Settings → Features can switch the library off; the Pages tab then offers the built-in templates inline.
    if (!id || !libraryOn) return undefined;
    const saveFirst = async (): Promise<void> => {
      if (!(await persist())) {
        throw new Error("Your latest changes couldn't be saved, so nothing was changed. Try again in a moment.");
      }
    };
    return {
      save: async (pageIds, draft) => {
        await saveFirst();
        return saveLibraryItem({
          proposalId: id,
          pageIds,
          name: draft.name,
          ...(draft.description ? { description: draft.description } : {}),
          visibility: draft.visibility,
        });
      },
      suggest: async (pageIds) => {
        await saveFirst();
        return suggestLibraryDetails({ proposalId: id, pageIds });
      },
      insert: async (item, requestedIndex) => {
        await saveFirst();
        const current = latestDocRef.current;
        const atIndex =
          requestedIndex ?? (current ? insertIndexAfterSelection(current, selectionRef.current) : undefined);
        const result = await insertLibraryItem(id, { itemId: item.id, ...(atIndex !== undefined ? { atIndex } : {}) });
        setMeta(result.proposal);
        replaceDoc(result.proposal.document);
        notifySaved();
        const firstId = result.insertedPageIds[0] ?? result.proposal.document.pages[result.atIndex ?? atIndex ?? -1]?.id;
        if (firstId) {
          setSelection({ kind: 'page', id: firstId });
          window.requestAnimationFrame(() => revealPage(firstId));
        }
        setInsertReport(result.skippedKeys.length > 0 ? { name: item.name, skippedKeys: result.skippedKeys } : null);
      },
    };
  }, [id, libraryOn, persist, replaceDoc, revealPage, notifySaved]);

  // BESTie's library cards act through the open editor, so they flush pending
  // edits and refresh this document (syncing with an in-page registry).
  useEffect(() => {
    if (!id || !library) return undefined;
    return registerOpenEditor({ proposalId: id, currentDoc: () => latestDocRef.current, library });
  }, [id, library]);

  /* ---------------- drag & drop ---------------- */

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => {
    if (!activeDrag) return;
    let raf = 0;
    const onPointerMove = (e: PointerEvent) => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const desk = deskRef.current;
        if (!desk) return;
        const blockType =
          activeDrag.kind === 'new-block'
            ? activeDrag.blockType
            : (doc && findBlock(doc, activeDrag.blockId)?.block.type) || 'stack';
        const target = probeDropTarget(e.clientX, e.clientY, desk, blockType);
        dropTargetRef.current = target;
        setDropTarget(target);
      });
    };
    window.addEventListener('pointermove', onPointerMove);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [activeDrag, doc]);

  const onDragStart = (e: DragStartEvent) => {
    const data = e.active.data.current as ActiveDrag | undefined;
    if (data) setActiveDrag(data);
  };

  const onDragEnd = (e: DragEndEvent) => {
    void e;
    const target = dropTargetRef.current;
    const drag = activeDrag;
    setActiveDrag(null);
    setDropTarget(null);
    dropTargetRef.current = null;
    if (!drag || !target || !doc) return;
    if (drag.kind === 'new-block') {
      const def = BLOCKS_BY_TYPE.get(drag.blockType);
      if (!def) return;
      const node: BlockNode = {
        id: newBlockId(def.type),
        type: def.type,
        props: structuredClone(def.defaults.props),
        ...(def.defaults.style ? { style: { ...def.defaults.style } } : {}),
        ...(def.acceptsChildren ? { children: structuredClone(def.defaults.children ?? []) } : {}),
      };
      apply(insertBlock(doc, target.pageId, target.parentBlockId, target.index, node));
      setSelection({ kind: 'block', id: node.id });
    } else {
      apply(moveBlock(doc, drag.blockId, target.pageId, target.parentBlockId, target.index));
      setSelection({ kind: 'block', id: drag.blockId });
    }
  };

  /* ---------------- keyboard ---------------- */

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable) return;
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) history.redo();
        else history.undo();
        setSaveState('dirty');
      } else if (mod && e.key === 'd' && selection?.kind === 'block' && doc) {
        e.preventDefault();
        apply(duplicateBlock(doc, selection.id));
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && selection?.kind === 'block' && doc) {
        e.preventDefault();
        apply(removeBlock(doc, selection.id));
        setSelection(null);
      } else if (e.key === 'Escape') {
        setSelection(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [apply, doc, history, selection]);

  /* ---------------- render ---------------- */

  useEffect(() => {
    if (hoveredId) lastHoverRef.current = hoveredId;
  }, [hoveredId]);
  const gutterId = hoveredId ?? (gutterLock ? lastHoverRef.current : null);

  const startPaneResize = useCallback(
    (e: ReactPointerEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = paneWidth;
      const onMove = (ev: PointerEvent) => {
        const next = Math.min(600, Math.max(260, startWidth + (startX - ev.clientX)));
        setPaneWidth(next);
      };
      const onUp = (ev: PointerEvent) => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        try {
          localStorage.setItem(
            'dg.paneWidth',
            String(Math.min(600, Math.max(260, startWidth + (startX - ev.clientX)))),
          );
        } catch {
          /* per-viewer convenience only */
        }
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [paneWidth],
  );

  const insertAfter = useCallback(
    (blockId: string, blockType: string) => {
      if (!doc) return;
      const def = BLOCKS_BY_TYPE.get(blockType);
      const found = findBlock(doc, blockId);
      if (!def || !found) return;
      const node: BlockNode = {
        id: newBlockId(def.type),
        type: def.type,
        props: structuredClone(def.defaults.props),
        ...(def.defaults.style ? { style: { ...def.defaults.style } } : {}),
        ...(def.acceptsChildren ? { children: structuredClone(def.defaults.children ?? []) } : {}),
      };
      apply(insertBlock(doc, found.pageId, found.parentBlockId, found.index + 1, node));
      setSelection({ kind: 'block', id: node.id });
      setInsertMenu(null);
    },
    [apply, doc],
  );

  const dragLabel = useMemo(() => {
    if (!activeDrag) return null;
    if (activeDrag.kind === 'new-block') return BLOCKS_BY_TYPE.get(activeDrag.blockType)?.name ?? activeDrag.blockType;
    if (doc) {
      const found = findBlock(doc, activeDrag.blockId);
      if (found) return BLOCKS_BY_TYPE.get(found.block.type)?.name ?? found.block.type;
    }
    return 'Block';
  }, [activeDrag, doc]);

  // A phone gets the preview-and-quick-edits builder over the same state; the
  // canvas, drag and drop and the data panel need a mouse and a wide screen.
  // The Salesforce embed is a full-screen desktop modal; the phone builder's
  // links lead into the app, which the embed does not have.
  if (isPhone && !embedding) {
    if (!id || !doc) return <LoadingState label="Loading the proposal…" />;
    return (
      <PhoneBuilder
        proposalId={id}
        doc={doc}
        meta={meta}
        saveState={saveState}
        onSave={() => void persist()}
        onChange={apply}
        onPricingResult={onPricingResult}
        exportHref={`/print/${id}`}
        exportIssues={exportIssues ?? []}
        onAskAssistant={FEATURES.assistant ? requestAssistantOpen : undefined}
      />
    );
  }

  return (
    <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar
          doc={doc}
          editMode={editMode}
          onEditMode={setEditMode}
          onRename={(name) => doc && apply({ ...doc, name }, 'docname')}
          undo={() => {
            history.undo();
            setSaveState('dirty');
          }}
          redo={() => {
            history.redo();
            setSaveState('dirty');
          }}
          canUndo={history.canUndo}
          canRedo={history.canRedo}
          saveState={saveState}
          onSave={() => void persist()}
          pageCount={resolved?.pages.length ?? 0}
          warnings={resolved?.warnings ?? []}
          zoom={zoom}
          onZoom={setZoom}
          pricingOpen={pricingOpen}
          onPricingOpen={setPricingOpen}
          onReset={() => setPending({ action: PROPOSAL_ACTION.reset, busy: false, error: null })}
          {...(embedding
            ? {
                // Salesforce is already showing the opportunity, and deleting
                // stays a staff-app action (the Editor token cannot).
                onExport: embedding.onExportPdf,
                ...(embedding.assistant ? { assistant: embedding.assistant } : {}),
                onDone: () => {
                  void persist().then((saved) => {
                    if (saved) embedding.onDone();
                  });
                },
              }
            : {
                backHref: '/proposals',
                onDelete: () => setPending({ action: PROPOSAL_ACTION.delete, busy: false, error: null }),
                ...(id ? { exportHref: `/print/${id}` } : {}),
                ...(meta?.opportunityId ? { opportunityHref: salesforceOpportunityUrl(meta.opportunityId) } : {}),
              })}
          exportIssues={exportIssues ?? []}
          onExportBlocked={() => setShowExportIssues(true)}
        />
        {showExportIssues && exportIssues && exportIssues.length > 0 ? (
          <ExportIssuesNotice issues={exportIssues} onDismiss={() => setShowExportIssues(false)} />
        ) : null}
        {insertReport ? <LibraryInsertNotice report={insertReport} onDismiss={() => setInsertReport(null)} /> : null}
        <ConfirmDialog
          open={pending !== null}
          title={pending ? ACTION_COPY[pending.action].title : ''}
          message={pending ? ACTION_COPY[pending.action].message : ''}
          confirmLabel={pending ? ACTION_COPY[pending.action].confirmLabel : ''}
          tone="danger"
          pending={pending?.busy ?? false}
          {...(pending?.error ? { error: pending.error } : {})}
          onConfirm={() => void runPending()}
          onCancel={() => setPending(null)}
        />
        <div className="flex min-h-0 flex-1">
          {doc && (
            <Panel
              doc={doc}
              selection={selection}
              onSelect={setSelection}
              onChange={apply}
              onRevealBlock={revealBlock}
              onRevealPage={revealPage}
              library={library}
            />
          )}
          <Desk deskRef={deskRef} zoom={zoom}>
            {doc && (
              <DocumentView
                doc={doc}
                editable={editMode}
                selectedId={selection?.kind === 'block' ? selection.id : null}
                hoveredId={hoveredId}
                onSelect={(blockId, blockKey) =>
                  setSelection(
                    blockId ? { kind: 'block', id: blockId, ...(blockKey ? { key: blockKey } : {}) } : null,
                  )
                }
                onHover={setHoveredId}
                onSlotEdit={onDocEdit}
                richEditor={RichTextEditor}
              />
            )}
          </Desk>
          {/* Pricing always forces the pane open, whatever the collapse choice. */}
          {pricingOpen || !inspectorCollapsed ? (
            <>
              <div
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize tool pane"
                className="w-1 shrink-0 cursor-col-resize bg-border/60 transition-colors hover:bg-primary/50 active:bg-primary"
                onPointerDown={startPaneResize}
              />
              <aside className="flex shrink-0 flex-col overflow-hidden bg-card" style={{ width: paneWidth }}>
                <div className="flex shrink-0 items-center justify-end border-b px-1.5 py-1">
                  <button
                    type="button"
                    title="Collapse panel"
                    aria-label="Collapse panel"
                    className="grid size-7 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
                    onClick={() => {
                      setPricingOpen(false);
                      setInspectorCollapsed(true);
                    }}
                  >
                    <PanelRightClose className="size-4" />
                  </button>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto">
                  {doc && pricingOpen && id && meta ? (
                    <PricingPanel
                      proposalId={id}
                      pricing={meta.pricing}
                      totals={meta.totals}
                      onResult={onPricingResult}
                    />
                  ) : doc ? (
                    <ImageUploadContext.Provider value={uploadProposalImage}>
                      <Inspector doc={doc} selection={selection} onChange={apply} onSelect={setSelection} onOpenPricing={() => setPricingOpen(true)} />
                    </ImageUploadContext.Provider>
                  ) : null}
                </div>
              </aside>
            </>
          ) : (
            <div className="flex w-10 shrink-0 flex-col items-center border-l bg-card py-2">
              <button
                type="button"
                title="Expand panel"
                aria-label="Expand panel"
                className="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
                onClick={() => setInspectorCollapsed(false)}
              >
                <PanelRightOpen className="size-4" />
              </button>
            </div>
          )}
        </div>
      </div>
      <HoverGutter
        targetId={editMode ? gutterId : null}
        deskRef={deskRef}
        onLock={setGutterLock}
        onOpenInsert={(blockId, at) => setInsertMenu({ blockId, at })}
        hidden={activeDrag !== null || insertMenu !== null}
      />
      {insertMenu && (
        <InsertMenu
          at={insertMenu.at}
          onPick={(blockType) => insertAfter(insertMenu.blockId, blockType)}
          onClose={() => setInsertMenu(null)}
        />
      )}
      {activeDrag && dropTarget && (
        <div
          className="pointer-events-none fixed z-50 rounded-full bg-primary shadow-[0_0_0_2px_rgba(255,255,255,0.9)]"
          style={{
            top: dropTarget.indicator.top,
            left: dropTarget.indicator.left,
            width: dropTarget.indicator.width,
            height: dropTarget.indicator.height,
          }}
        />
      )}
      <DragOverlay dropAnimation={null}>
        {dragLabel && (
          <div className="rounded-md border bg-card px-2.5 py-1.5 text-xs font-medium shadow-md">
            {dragLabel}
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}
